// ElectricalEnergy.js
import React, { useState, useCallback } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds,MobileUnderCalAds } from "../../Ads";
const TITLE =
  "Menghitung Energi Listrik - Hitung Energi Listrik, Daya, atau Waktu";
const DESCRIPTION =
  "Kalkulator energi listrik komprehensif: Hitung energi listrik, daya, atau waktu berdasarkan nilai input daya, waktu, atau energi listrik. Alat yang akurat dan mudah digunakan.";
const KEYWORDS =
  "energi listrik, menghitung energi listrik, kalkulator energi listrik, menghitung daya, menghitung waktu, listrik";
const OG_URL = "https://do-calculate.com/calculator/id/electricalenergy";
function ElectricalEnergyCalculator() {
  const [power, setPower] = useState("");
  const [time, setTime] = useState("");
  const [energy, setEnergy] = useState("");
  const [calculationMode, setCalculationMode] = useState("calculateEnergy");
  const [error, setError] = useState("");
  const [showSteps, setShowSteps] = useState(false);
  const updateCalculations = useCallback(() => {
    setError("");
    const powerValue = parseFloat(power) || 0;
    const timeValue = parseFloat(time) || 0;
    const energyValue = parseFloat(energy) || 0;
    switch (calculationMode) {
      case "calculateEnergy":
        if (powerValue > 0 && timeValue > 0) {
          const calculatedEnergy = powerValue * timeValue;
          setEnergy(Math.round(calculatedEnergy * 100) / 100);
        } else {
          setError("Harap masukkan daya dan waktu dengan benar");
        }
        break;
      case "calculatePower":
        if (energyValue > 0 && timeValue > 0) {
          const calculatedPower = energyValue / timeValue;
          setPower(Math.round(calculatedPower * 100) / 100);
        } else {
          setError("Harap masukkan energi dan waktu dengan benar");
        }
        break;
      case "calculateTime":
        if (energyValue > 0 && powerValue > 0) {
          const calculatedTime = energyValue / powerValue;
          setTime(Math.round(calculatedTime * 100) / 100);
        } else {
          setError("Harap masukkan energi dan daya dengan benar");
        }
        break;
      default:
        break;
    }
  }, [power, time, energy, calculationMode]);
  const handleCalculateClick = () => {
    updateCalculations();
    setShowSteps(true);
  };
  const handlePowerChange = (e) => setPower(e.target.value);
  const handleTimeChange = (e) => setTime(e.target.value);
  const handleEnergyChange = (e) => setEnergy(e.target.value);
  const handleModeChange = (e) => setCalculationMode(e.target.value);
  const getSolutionSteps = () => {
    switch (calculationMode) {
      case "calculateEnergy":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Energi Listrik:</h4>
            <ol>
              <li>Masukkan daya (dalam watt) pada kolom yang disediakan.</li>
              <li>Masukkan waktu (dalam detik) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan energi listrik.</li>
              <li>Energi listrik sama dengan daya × waktu.</li>
            </ol>
          </div>
        );
      case "calculatePower":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Daya:</h4>
            <ol>
              <li>Masukkan energi listrik (dalam joule) pada kolom yang disediakan.</li>
              <li>Masukkan waktu (dalam detik) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan daya.</li>
              <li>Daya sama dengan energi listrik / waktu.</li>
            </ol>
          </div>
        );
      case "calculateTime":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Waktu:</h4>
            <ol>
              <li>Masukkan energi listrik (dalam joule) pada kolom yang disediakan.</li>
              <li>Masukkan daya (dalam watt) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan waktu.</li>
              <li>Waktu sama dengan energi listrik / daya.</li>
            </ol>
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  Kalkulator energi listrik ini memungkinkan Anda melakukan tiga jenis perhitungan
                  berdasarkan nilai input. Pilih jenis perhitungan yang sesuai dan masukkan
                  nilai yang tersedia untuk menghitung nilai yang hilang dengan akurat dan mudah.
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Energi Listrik dengan Daya dan Waktu yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui daya (dalam watt) dan waktu (dalam detik), Anda dapat
                  menghitung energi listrik (dalam joule) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Energi Listrik = Daya × Waktu</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Daya dengan Energi Listrik dan Waktu yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui energi listrik (dalam joule) dan waktu (dalam detik),
                  Anda dapat menghitung daya (dalam watt) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Daya = Energi Listrik / Waktu</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Waktu dengan Energi Listrik dan Daya yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui energi listrik (dalam joule) dan daya (dalam watt),
                  Anda dapat menghitung waktu (dalam detik) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Waktu = Energi Listrik / Daya</b>
                </p>
                <p>
                  Gunakan alat ini untuk memasukkan nilai yang tersedia dan menghitung nilai yang hilang
                  dengan akurat, memudahkan Anda melakukan perhitungan terkait energi listrik.
                </p>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <hr />
                <div >
                  <div className="form-group align-items-center">
                    <b>
                      <label
                        className="form-label ms-2"
                        htmlFor="calculationMode"
                      >
                        Pilih Jenis Perhitungan
                      </label>
                    </b>
                    <div id="calculationMode">
                      <br />
                      <div>
                        <input
                          type="radio"
                          id="calculateEnergy"
                          name="calculationMode"
                          value="calculateEnergy"
                          checked={calculationMode === "calculateEnergy"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateEnergy" className="ms-2">
                          Hitung Energi Listrik
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          id="calculatePower"
                          name="calculationMode"
                          value="calculatePower"
                          checked={calculationMode === "calculatePower"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculatePower" className="ms-2">
                          Hitung Daya
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          id="calculateTime"
                          name="calculationMode"
                          value="calculateTime"
                          checked={calculationMode === "calculateTime"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateTime" className="ms-2">
                          Hitung Waktu
                        </label>
                      </div>
                    </div>
                  </div>
                  <br />
                  {calculationMode !== "calculatePower" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2" htmlFor="powerInput">
                          Daya (Watt)
                        </label>
                      </b>
                      <input
                        id="powerInput"
                        type="number"
                        placeholder="1000"
                        value={power}
                        onChange={handlePowerChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  {calculationMode !== "calculateTime" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2" htmlFor="timeInput">
                          Waktu (Detik)
                        </label>
                      </b>
                      <input
                        id="timeInput"
                        type="number"
                        placeholder="3600"
                        value={time}
                        onChange={handleTimeChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  {calculationMode !== "calculateEnergy" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label
                          className="form-label ms-2"
                          htmlFor="energyInput"
                        >
                          Energi Listrik (Joule)
                        </label>
                      </b>
                      <input
                        id="energyInput"
                        type="number"
                        placeholder="3600000"
                        value={energy}
                        onChange={handleEnergyChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  <br />
                  <button
                    className="btn btn-success"
                    style={{ fontSize: "20px" }}
                    onClick={handleCalculateClick}
                  >
                    Hitung
                  </button>
                  <br/>                  <div>
                    { (
                      <>
                        {error !== "" && (
                          <p className="p-3 mb-2 bg-warning text-white">
                            Kesalahan: {error}
                          </p>
                        )}
                        {calculationMode === "calculateEnergy" &&
                          energy !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Energi Listrik (Joule): {energy} Joule
                            </p>
                          )}
                        {calculationMode === "calculatePower" &&
                          power !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Daya (Watt): {power} Watt
                            </p>
                          )}
                        {calculationMode === "calculateTime" && time !== "" && (
                          <p className="p-3 mb-2 bg-success text-white">
                            Waktu (Detik): {time} Detik
                          </p>
                        )}
                        {showSteps && getSolutionSteps()}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
         </div>
</div>
  );
}
export default ElectricalEnergyCalculator;